/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { FiArrowRight, FiCalendar, FiClock, FiMapPin } from 'react-icons/fi';
import { MdOutlineBeachAccess } from 'react-icons/md';
import PackageImage from '../common/PackageImage';
import PackageState from '../common/PackageState';
import { searchPackagesApi } from '../../api/packagesApi';
import { PACKAGE_ROUTES } from '../../constants/packageConstants';
import {
  DEFAULT_PACKAGE_PAX,
  buildPackageSearchQuery,
  buildPackageSearchRequest,
  daysNightsText,
  formatIsoDate,
  formatPrice,
  packageCurrency,
  packageDays,
  packageFromPrice,
  packageImageUrl,
  packageNights,
  packagePlace,
  prettyDestination,
} from '../../utils/packageUtils';
import '../../styles/packagesShared.scss';
import './PackageBrowse.scss';

/** Cards added to the grid each time the bottom of the list comes into view. */
const PAGE_SIZE = 9;
const ALL = '';

/** An empty destination asks the search API for the whole catalogue. */
const BROWSE_SEARCH = Object.freeze({
  destination: '',
  minDays: null,
  maxDays: null,
  maxPrice: null,
  pax: DEFAULT_PACKAGE_PAX,
});

const destinationOf = (pkg) => prettyDestination(pkg?.destination?.label);

function BrowseCard({ pkg, onOpen }) {
  const price = packageFromPrice(pkg);
  const days = packageDays(pkg);
  const place = packagePlace(pkg);
  const open = () => onOpen(pkg);

  return (
    <article className="pkg-browse-card">
      <button
        type="button"
        className="pkg-browse-card__media"
        onClick={open}
        aria-label={`View ${pkg.name || 'package'}`}
      >
        <PackageImage
          src={packageImageUrl(pkg.coverImage)}
          alt={pkg.name || ''}
        />
        <span className="pkg-browse-card__shade" aria-hidden />
        {days != null && (
          <span className="pkg-browse-card__duration">
            <FiClock size={12} />
            {daysNightsText(days, packageNights(pkg))}
          </span>
        )}
        <span className="pkg-browse-card__overlay">
          <strong className="pkg-browse-card__title">{pkg.name || '—'}</strong>
          {place && (
            <span className="pkg-browse-card__place">
              <FiMapPin size={12} /> {place}
            </span>
          )}
        </span>
      </button>

      <div className="pkg-browse-card__body">
        {pkg.summary && (
          <p className="pkg-browse-card__summary">{pkg.summary.trim()}</p>
        )}
        <span
          className={`pkg-browse-card__departure ${
            pkg.nextDeparture ? '' : 'pkg-browse-card__departure--none'
          }`}
        >
          <FiCalendar size={13} />
          {pkg.nextDeparture ? (
            <>
              Next departure <strong>{formatIsoDate(pkg.nextDeparture)}</strong>
            </>
          ) : (
            'No upcoming departure listed'
          )}
        </span>

        <div className="pkg-browse-card__foot">
          <div className="pkg-browse-card__price">
            <small>Starting from</small>
            <strong>
              {price != null ? formatPrice(price, packageCurrency(pkg)) : '—'}
            </strong>
            <small>per person</small>
          </div>
          <button type="button" className="pkg-browse-card__cta" onClick={open}>
            View Details <FiArrowRight />
          </button>
        </div>
      </div>
    </article>
  );
}

BrowseCard.propTypes = {
  pkg: PropTypes.object.isRequired,
  onOpen: PropTypes.func.isRequired,
};

function BrowseSkeleton({ count }) {
  return (
    <div className="pkg-browse__grid" aria-busy="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="pkg-browse-card pkg-browse-card--skeleton">
          <span className="pkg-skeleton pkg-browse-card__skeleton-media" />
          <div className="pkg-browse-card__body">
            <span className="pkg-skeleton" />
            <span className="pkg-skeleton pkg-skeleton--short" />
            <span className="pkg-skeleton pkg-skeleton--title" />
          </div>
        </div>
      ))}
    </div>
  );
}

BrowseSkeleton.propTypes = {
  count: PropTypes.number.isRequired,
};

/**
 * Every package in the catalogue, under the search form on the landing page.
 *
 * The search API has no paging: one call with an empty destination returns
 * the whole list. "Infinite scroll" is therefore on the client — PAGE_SIZE
 * cards are rendered, and the next batch is added when a sentinel below the
 * grid scrolls into view. Destination chips narrow the list without a new call.
 */
function PackageBrowse() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, read: null });
  const [attempt, setAttempt] = useState(0);
  const [destination, setDestination] = useState(ALL);
  const [shown, setShown] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true }));
    searchPackagesApi(
      buildPackageSearchRequest(BROWSE_SEARCH),
      controller.signal,
    ).then((read) => {
      if (controller.signal.aborted) return;
      setState({ loading: false, read });
    });
    return () => controller.abort();
  }, [attempt]);

  const { loading, read } = state;
  const searchId = read?.data?.searchId || '';
  const packages = useMemo(
    () =>
      Array.isArray(read?.data?.packages)
        ? read.data.packages.filter((p) => p?.packageKey)
        : [],
    [read],
  );

  // chips: each destination in the catalogue, most packages first
  const destinations = useMemo(() => {
    const counts = new Map();
    packages.forEach((p) => {
      const d = destinationOf(p);
      if (d) counts.set(d, (counts.get(d) || 0) + 1);
    });
    return [...counts.entries()]
      .sort(([a, x], [b, y]) => y - x || a.localeCompare(b))
      .map(([label, count]) => ({ label, count }));
  }, [packages]);

  const filtered = useMemo(
    () =>
      destination === ALL
        ? packages
        : packages.filter((p) => destinationOf(p) === destination),
    [packages, destination],
  );
  const visible = filtered.slice(0, shown);
  const hasMore = shown < filtered.length;

  // a new chip or a fresh list starts from the first batch again
  useEffect(() => setShown(PAGE_SIZE), [destination, packages]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!hasMore || !node || typeof IntersectionObserver === 'undefined')
      return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown((n) => n + PAGE_SIZE);
        }
      },
      { rootMargin: '0px 0px 320px 0px' }, // start loading a little early
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, shown]);

  const openPackage = (pkg) => {
    const params = new URLSearchParams(
      buildPackageSearchQuery({
        ...BROWSE_SEARCH,
        destination: pkg.destination?.label || '',
      }),
    );
    params.set('key', pkg.packageKey);
    params.set('sid', searchId);
    navigate(`${PACKAGE_ROUTES.DETAILS}?${params.toString()}`);
  };

  // nothing in the catalogue: say nothing rather than an empty section
  if (!loading && read?.ok && packages.length === 0) return null;

  return (
    <section className="pkg-browse" aria-labelledby="pkg-browse-title">
      <header className="pkg-browse__head">
        <div>
          <h2 id="pkg-browse-title">Explore holiday packages</h2>
          {!loading && read?.ok && (
            <p>
              {filtered.length} package{filtered.length === 1 ? '' : 's'}
              {destination !== ALL ? ` in ${destination}` : ' to choose from'}
            </p>
          )}
        </div>
      </header>

      {!loading && destinations.length > 1 && (
        <div
          className="pkg-browse__chips"
          role="group"
          aria-label="Filter by destination"
        >
          {[{ label: ALL, count: packages.length }, ...destinations].map(
            (d) => (
              <button
                key={d.label || 'all'}
                type="button"
                aria-pressed={destination === d.label}
                className={`pkg-browse__chip ${
                  destination === d.label ? 'pkg-browse__chip--active' : ''
                }`}
                onClick={() => setDestination(d.label)}
              >
                {d.label || 'All'}
                <span>{d.count}</span>
              </button>
            ),
          )}
        </div>
      )}

      {loading && <BrowseSkeleton count={6} />}

      {!loading && read && !read.ok && (
        <PackageState
          icon={<MdOutlineBeachAccess />}
          title={
            read.network
              ? 'We could not reach the server'
              : 'Packages could not be loaded'
          }
          message={
            read.network
              ? 'Check your connection and try again.'
              : read.message || 'Please try again in a moment.'
          }
          reference={read.correlationId}
          primaryText="Try again"
          onPrimary={() => setAttempt((n) => n + 1)}
        />
      )}

      {!loading && visible.length > 0 && (
        <>
          <div className="pkg-browse__grid">
            {visible.map((pkg) => (
              <BrowseCard key={pkg.packageKey} pkg={pkg} onOpen={openPackage} />
            ))}
          </div>
          {hasMore && (
            <div ref={sentinelRef} className="pkg-browse__more">
              {typeof IntersectionObserver === 'undefined' ? (
                <button
                  type="button"
                  className="pkg-browse__more-btn"
                  onClick={() => setShown((n) => n + PAGE_SIZE)}
                >
                  Show more packages
                </button>
              ) : (
                <span className="pkg-browse__spinner" aria-label="Loading" />
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default PackageBrowse;
